import { DvaReducer, WholeReadonly } from '@/models/shared';
import { AnyAction } from 'redux';
import { EffectsCommandMap, Subscription } from 'dva';
import { FServiceAPI, FUtil } from '@freelog/tools-lib';
import { AVATAR_URL_PREFIX } from '@/constants';
import {
  CollectionCreatorPageModelState,
  ConnectState,
  ResourceDetailPageModelState,
} from '@/models/connect';
import { history } from '@@/core/history';
import dayjs from 'dayjs';
// import WXShareMgr from '@/utils/WXShareMgr';

export type CollectionDetailPageModelState = {
  page_State: 'loading' | 'details';
  user_Logged: boolean;
  resource_ID: string;
  resource_Popularity: number;
  resource_IsCollected: boolean;
  resource_Info: {
    title: string;
    cover: string;
    name: string;
    type: string[];
    tags: string[];
    about: string;
    isChoice: boolean;
    username: string;
    useAvatar: string;
    policyTitles: string[];
    error: '' | 'offline' | 'freeze' | 'unreleased';
    warning: '' | 'authException' | 'ownerFreeze';
    collection?: {
      status: 'finish' | 'unFinish';
      count: number;
    } | null;
  };
  resourceVersion_Info: {
    releaseTime: string;
    description: string;
    rawProperties: {
      key: string;
      name: string;
      value: string;
      description: string;
    }[];
    baseProperties: {
      key: string;
      name: string;
      value: string;
      description: string;
    }[];
    customOptions: {
      key: string;
      name: string;
      description: string;
      type: 'input' | 'select';
      input: string;
      select: string[];
    }[];
    updateDate: string;
  };

  collectionItemsSetting: {
    collection_sort_list: 'collection_sort_ascending' | 'collection_sort_descending';
    collection_item_title:
      | 'collection_item_title_rtitle'
      | 'collection_item_title_sn'
      | 'collection_item_title_empty'
      | 'collection_item_title_custom';
    collection_item_no_display:
      | 'collection_item_no_display_show'
      | 'collection_item_no_display_hide';
    collection_item_image_display:
      | 'collection_item_image_display_show'
      | 'collection_item_image_display_hide';
    collection_item_descr_display:
      | 'collection_item_descr_display_show'
      | 'collection_item_descr_display_hide';
    collection_view: 'collection_view_list' | 'collection_view_card';
  };
  collectionItems: {
    itemID: string;
    itemTitle: string;
    itemSerial: number;
    itemNumber: string;
    resourceInfo: {
      id: string;
      cover: string;
      name: string;
      title: string;
      version: string;
      policy: string[];
      type: string[];
      status: 0 | 1 | 2 | 4;
      authProblem: boolean;
      updateDate: string;
      username: string;
      useAvatar: string;
      isChoice: boolean;
      intro: string;
      releaseDateTime?: string;
    };
  }[];
  collectionItems_totalCount: number;
  collectionItems_pageCurrent: number;
  collectionItems_pageSize: number;

  resourceVersion_authorizationGraphShow: boolean;
  resourceVersion_dependencyGraphShow: boolean;
};

export interface ChangeAction extends AnyAction {
  type: 'change' | 'collectionDetailPage/change';
  payload: Partial<CollectionDetailPageModelState>;
}

export interface OnMount_Page_Action extends AnyAction {
  type: 'collectionDetailPage/onMount_Page';
  payload: {
    resourceID: string;
  };
}

export interface OnUnmount_Page_Action extends AnyAction {
  type: 'collectionDetailPage/onUnmount_Page';
}

export interface OnClickCollectAction extends AnyAction {
  type: 'collectionDetailPage/onClickCollect';
}

export interface FetchCollectInfoAction extends AnyAction {
  type: 'fetchCollectInfo';
}

export interface FetchCollectionInfoAction extends AnyAction {
  type: 'fetchCollectionInfo';
}

export interface FetchVersionInfoAction extends AnyAction {
  type: 'fetchVersionInfo';
}

export interface Fetch_CollectionItems_Action extends AnyAction {
  type: 'fetch_CollectionItems' | 'collectionDetailPage/fetch_CollectionItems';
}

interface CollectionDetailPageModelType {
  namespace: 'collectionDetailPage';
  state: CollectionDetailPageModelState;
  effects: {
    onMount_Page: (action: OnMount_Page_Action, effects: EffectsCommandMap) => void;
    onUnmount_Page: (action: OnUnmount_Page_Action, effects: EffectsCommandMap) => void;
    // fetchInfo: (action: FetchInfoAction, effects: EffectsCommandMap) => void;
    fetchCollectInfo: (action: FetchCollectInfoAction, effects: EffectsCommandMap) => void;
    onClickCollect: (action: OnClickCollectAction, effects: EffectsCommandMap) => void;
    fetchCollectionInfo: (action: FetchCollectionInfoAction, effects: EffectsCommandMap) => void;
    fetchVersionInfo: (action: FetchVersionInfoAction, effects: EffectsCommandMap) => void;
    fetch_CollectionItems: (
      action: Fetch_CollectionItems_Action,
      effects: EffectsCommandMap,
    ) => void;
  };
  reducers: {
    change: DvaReducer<CollectionDetailPageModelState, ChangeAction>;
  };
  subscriptions: {
    setup: Subscription;
  };
}

const initStates: CollectionDetailPageModelState = {
  page_State: 'loading',
  user_Logged: FUtil.Tool.getUserIDByCookies() !== -1,

  resource_ID: '',
  resource_Popularity: 0,
  resource_IsCollected: false,
  resource_Info: {
    cover: '',
    title: '',
    name: '',
    type: [],
    tags: [],
    about: '',
    isChoice: false,
    username: '',
    useAvatar: '',
    policyTitles: [],
    error: '',
    warning: '',
    collection: null,
  },
  resourceVersion_Info: {
    releaseTime: '',
    description: '',
    rawProperties: [],
    baseProperties: [],
    customOptions: [],
    updateDate: '',
  },

  collectionItemsSetting: {
    collection_sort_list: 'collection_sort_ascending',
    collection_item_title: 'collection_item_title_rtitle',
    collection_item_no_display: 'collection_item_no_display_show',
    collection_item_image_display: 'collection_item_image_display_show',
    collection_item_descr_display: 'collection_item_descr_display_show',
    collection_view: 'collection_view_list',
  },
  collectionItems: [],
  collectionItems_totalCount: 0,
  collectionItems_pageCurrent: 1,
  collectionItems_pageSize: 10,

  resourceVersion_authorizationGraphShow: true,
  resourceVersion_dependencyGraphShow: true,
};

const Model: CollectionDetailPageModelType = {
  namespace: 'collectionDetailPage',
  state: initStates,
  effects: {
    *onMount_Page({ payload }: OnMount_Page_Action, { put, call }: EffectsCommandMap) {
      yield put({
        type: 'change',
        payload: {
          resource_ID: payload.resourceID,
          user_Logged: FUtil.Tool.getUserIDByCookies() !== -1,
        },
      });
      yield put<FetchCollectInfoAction>({
        type: 'fetchCollectInfo',
      });

      yield put<FetchCollectionInfoAction>({
        type: 'fetchCollectionInfo',
      });

      yield put<FetchVersionInfoAction>({
        type: 'fetchVersionInfo',
      });
    },
    *onUnmount_Page({}: OnUnmount_Page_Action, { put }: EffectsCommandMap) {
      yield put<ChangeAction>({
        type: 'change',
        payload: initStates,
      });
    },
    *onClickCollect({}: OnClickCollectAction, { select, call, put }: EffectsCommandMap) {
      const { collectionDetailPage }: ConnectState = yield select(
        ({ collectionDetailPage }: ConnectState) => ({
          collectionDetailPage,
        }),
      );

      if (!collectionDetailPage.resource_IsCollected) {
        const params: Parameters<typeof FServiceAPI.Collection.collectResource>[0] = {
          resourceId: collectionDetailPage.resource_ID,
        };
        const { ret, errCode, data } = yield call(FServiceAPI.Collection.collectResource, params);
        // console.log(ret, errCode, data, 'DDDDDDDDDDDDDDDDDDDDDDDDD');
        if (ret !== 0 || errCode !== 0) {
          return;
        }
      } else {
        const params: Parameters<typeof FServiceAPI.Collection.deleteCollectResource>[0] = {
          resourceId: collectionDetailPage.resource_ID,
        };
        const { ret, errCode, data } = yield call(
          FServiceAPI.Collection.deleteCollectResource,
          params,
        );
      }

      yield put<FetchCollectInfoAction>({
        type: 'fetchCollectInfo',
      });
    },
    *fetchCollectInfo({}: FetchCollectInfoAction, { call, select, put }: EffectsCommandMap) {
      const { collectionDetailPage }: ConnectState = yield select(
        ({ collectionDetailPage }: ConnectState) => ({
          collectionDetailPage,
        }),
      );

      let isCollected: boolean = false;
      if (collectionDetailPage.user_Logged) {
        const params1: Parameters<typeof FServiceAPI.Collection.isCollected>[0] = {
          resourceIds: collectionDetailPage.resource_ID,
        };

        const { data: data1 } = yield call(FServiceAPI.Collection.isCollected, params1);

        isCollected = data1[0].isCollected;
      }

      const params2: Parameters<typeof FServiceAPI.Collection.collectedCount>[0] = {
        resourceId: collectionDetailPage.resource_ID,
      };

      const { data: data2 } = yield call(FServiceAPI.Collection.collectedCount, params2);
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          resource_IsCollected: isCollected,
          resource_Popularity: data2,
        },
      });
    },
    *fetchCollectionInfo({}: FetchCollectionInfoAction, { select, call, put }: EffectsCommandMap) {
      const { collectionDetailPage }: ConnectState = yield select(
        ({ collectionDetailPage }: ConnectState) => ({
          collectionDetailPage,
        }),
      );

      const params: Parameters<typeof FServiceAPI.Resource.info>[0] = {
        resourceIdOrName: collectionDetailPage.resource_ID,
      };

      const {
        data: data_ResourceDetail,
      }: {
        data: {
          resourceId: string;
          resourceName: string;
          resourceTitle: string;
          resourceType: string[];
          resourceTypeCode: string;
          coverImages: string[];
          resourceVersions: {
            createDate: string;
            version: string;
            versionId: string;
          }[];
          status: number;
          operationType: number;
          baseUpcastResources: {
            resourceId: string;
            resourceName: string;
          }[];
          createDate: string;
          updateDate: string;
          latestVersion: string;
          tags: string[];
          policies: {
            policyId: string;
            policyName: string;
            status: 0 | 1;
          }[];
          intro: string;
          userId: number;
          username: string;
          subjectType: number;
          serializeStatus: number;
        };
      } = yield call(FServiceAPI.Resource.info, params);
      if ((data_ResourceDetail.status & 2) === 2) {
        history.replace(
          FUtil.LinkTo.resourceFreeze({
            resourceID: collectionDetailPage.resource_ID,
          }),
        );
        return;
      }
      const params1: Parameters<typeof FServiceAPI.Resource.batchAuth>[0] = {
        resourceIds: collectionDetailPage.resource_ID,
      };
      const {
        data: data_auth,
      }: {
        data: {
          isAuth: boolean;
          resourceId: string;
        }[];
      } = yield call(FServiceAPI.Resource.batchAuth, params1);
      const params3: Parameters<typeof FServiceAPI.User.batchUserList>[0] = {
        userIds: String(data_ResourceDetail.userId),
      };

      const {
        data: data_batchUserList,
      }: {
        data: {
          status: number;
          userId: number;
        }[];
      } = yield call(FServiceAPI.User.batchUserList, params3);
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          page_State: 'details',
          resource_Info: {
            cover:
              data_ResourceDetail.coverImages.length > 0
                ? data_ResourceDetail.coverImages[0]
                : FUtil.Domain.STATIC_ORIGIN + '/static/default_cover.png',
            name: data_ResourceDetail.resourceName,
            title: data_ResourceDetail.resourceTitle || '',
            type: data_ResourceDetail.resourceType,
            tags: data_ResourceDetail.tags,
            about: data_ResourceDetail.intro,
            isChoice: data_ResourceDetail.operationType === 5,
            username: data_ResourceDetail.username,
            useAvatar: `${AVATAR_URL_PREFIX}${data_ResourceDetail.userId}`,
            policyTitles: data_ResourceDetail.policies
              .filter((p) => {
                return p.status === 1;
              })
              .map((p) => {
                return p.policyName;
              }),
            error:
              data_ResourceDetail.status === 0
                ? 'unreleased'
                : data_ResourceDetail.status === 2
                ? 'freeze'
                : data_ResourceDetail.status === 4
                ? 'offline'
                : '',
            warning:
              data_batchUserList[0].status === 1
                ? 'ownerFreeze'
                : !(data_auth[0] && data_auth[0].isAuth)
                ? 'authException'
                : '',
            collection:
              data_ResourceDetail.subjectType === 4
                ? {
                    status: data_ResourceDetail.serializeStatus === 1 ? 'finish' : 'unFinish',
                    count: -1,
                  }
                : null,
          },
          // resource_baseUpcastResources,
        },
      });
    },
    *fetchVersionInfo({}: FetchVersionInfoAction, { select, call, put }: EffectsCommandMap) {
      const { collectionDetailPage }: ConnectState = yield select(
        ({ collectionDetailPage }: ConnectState) => ({
          collectionDetailPage,
        }),
      );

      const params: Parameters<typeof FServiceAPI.Resource.resourceVersionInfo1>[0] = {
        version: '1.0.0',
        resourceId: collectionDetailPage.resource_ID,
      };
      const {
        data: data_versionInfo,
      }: {
        data: {
          customPropertyDescriptors: {
            key: string;
            name: string;
            defaultValue: string;
            type: 'editableText' | 'readonlyText' | 'radio' | 'checkbox' | 'select';
            candidateItems: string[];
            remark: string;
          }[];
          systemProperty: { [key: string]: string };
          systemPropertyDescriptors: {
            defaultValue: number | string;
            key: string;
            name: string;
            remark: string;
            valueDisplay: string;
            valueUnit: string;
          }[];
          createDate: string;
          updateDate: string;
          description: string;
          catalogueProperty: {
            collection_item_count: number;
            collection_sort_list?: 'collection_sort_ascending' | 'collection_sort_descending';
            collection_item_title?:
              | 'collection_item_title_rtitle'
              | 'collection_item_title_sn'
              | 'collection_item_title_empty'
              | 'collection_item_title_custom';
            collection_item_no_display?:
              | 'collection_item_no_display_show'
              | 'collection_item_no_display_hide';
            collection_item_image_display?:
              | 'collection_item_image_display_show'
              | 'collection_item_image_display_hide';
            collection_item_descr_display?:
              | 'collection_item_descr_display_show'
              | 'collection_item_descr_display_hide';
            collection_view?: 'collection_view_list' | 'collection_view_card';
          };
        };
      } = yield call(FServiceAPI.Resource.resourceVersionInfo1, params);

      yield put<ChangeAction>({
        type: 'change',
        payload: {
          resourceVersion_Info: {
            releaseTime: FUtil.Format.formatDateTime(data_versionInfo.createDate),
            description: data_versionInfo.description,
            rawProperties: data_versionInfo.systemPropertyDescriptors
              .filter((spd) => {
                return spd.defaultValue !== '';
              })
              .map((spd) => {
                return {
                  key: spd.key,
                  name: spd.name,
                  value: spd.valueDisplay,
                  description: spd.remark,
                };
              }),
            baseProperties: data_versionInfo.customPropertyDescriptors
              .filter((p) => {
                return p.type === 'readonlyText' && p.defaultValue !== '';
              })
              .map((p: any) => {
                // console.log(p, 'PPPPP()*UOI');
                return {
                  key: p.key,
                  name: p.name,
                  value: p.defaultValue,
                  description: p.remark,
                };
              }),
            customOptions: data_versionInfo.customPropertyDescriptors
              .filter((i) => {
                return i.type !== 'readonlyText';
              })
              .map((i) => {
                return {
                  key: i.key,
                  name: i.name,
                  description: i.remark,
                  type: i.type === 'editableText' ? 'input' : 'select',
                  input: i.defaultValue,
                  select: i.candidateItems,
                };
              }),
            updateDate: dayjs(data_versionInfo.updateDate).format(FUtil.Predefined.momentDateFormat),
          },
          collectionItems_pageSize:
            data_versionInfo.catalogueProperty.collection_view === 'collection_view_card' ? 6 : 10,
          collectionItemsSetting: {
            collection_sort_list:
              data_versionInfo.catalogueProperty.collection_sort_list ||
              'collection_sort_ascending',
            collection_item_title:
              data_versionInfo.catalogueProperty.collection_item_title ||
              'collection_item_title_rtitle',
            collection_item_no_display:
              data_versionInfo.catalogueProperty.collection_item_no_display ||
              'collection_item_no_display_show',
            collection_item_image_display:
              data_versionInfo.catalogueProperty.collection_item_image_display ||
              'collection_item_image_display_show',
            collection_item_descr_display:
              data_versionInfo.catalogueProperty.collection_item_descr_display ||
              'collection_item_descr_display_show',
            collection_view:
              data_versionInfo.catalogueProperty.collection_view || 'collection_view_list',
          },
        },
      });
      yield put<Fetch_CollectionItems_Action>({
        type: 'fetch_CollectionItems',
      });
    },
    *fetch_CollectionItems(
      {}: Fetch_CollectionItems_Action,
      { select, call, put }: EffectsCommandMap,
    ) {
      const { collectionDetailPage }: ConnectState = yield select(
        ({ collectionDetailPage }: ConnectState) => ({
          collectionDetailPage,
        }),
      );

      let data_collectionItems: {
        createDate: string;
        itemId: string;
        itemTitle: string;
        sortId: number;
        number: string;
        mountResourceInfo: {
          resourceId: string;
        };
      }[] = [];

      const parmas: Parameters<typeof FServiceAPI.Resource.getCollectionItems>[0] = {
        resourceId: collectionDetailPage.resource_ID,
        skip:
          (collectionDetailPage.collectionItems_pageCurrent - 1) *
          collectionDetailPage.collectionItems_pageSize,
        limit: collectionDetailPage.collectionItems_pageSize,
        // sortType: (collectionDetailPage.collectionItemsSetting.collection_sorting === 'collection_sort_newest' ||
        //   collectionDetailPage.collectionItemsSetting.collection_sorting === 'collection_sort_title_reverse' ||
        //   collectionDetailPage.collectionItemsSetting.collection_sorting === 'collection_sort_manual_reverse') ? -1 : 1,
        sortType:
          collectionDetailPage.collectionItemsSetting.collection_sort_list ===
          'collection_sort_descending'
            ? -1
            : 1,
        isLoadLatestVersionInfo: 1,
      };

      const {
        ret,
        errCode,
        data,
      }: {
        ret: number;
        errCode: number;
        data: {
          skip: number;
          limit: number;
          totalItem: number;
          dataList: {
            createDate: string;
            itemId: string;
            itemTitle: string;
            sortId: number;
            number: string;
            mountResourceInfo: {
              resourceId: string;
            };
          }[];
        };
      } = yield call(FServiceAPI.Resource.getCollectionItems, parmas);

      if (data.dataList.length > 0) {
        data_collectionItems = data.dataList;
      } else if (collectionDetailPage.collectionItems_pageCurrent > 1) {
        yield put<ChangeAction>({
          type: 'change',
          payload: {
            collectionItems_pageCurrent: 1,
          },
        });

        const parmas: Parameters<typeof FServiceAPI.Resource.getCollectionItems>[0] = {
          resourceId: collectionDetailPage.resource_ID,
          skip:
            (collectionDetailPage.collectionItems_pageCurrent - 1) *
            collectionDetailPage.collectionItems_pageSize,
          limit: collectionDetailPage.collectionItems_pageSize,
          isLoadLatestVersionInfo: 1,
        };

        const {
          ret,
          errCode,
          data: data1,
        }: {
          ret: number;
          errCode: number;
          data: {
            skip: number;
            limit: number;
            totalItem: number;
            dataList: {
              createDate: string;
              itemId: string;
              itemTitle: string;
              sortId: number;
              number: string;
              mountResourceInfo: {
                resourceId: string;
              };
            }[];
          };
        } = yield call(FServiceAPI.Resource.getCollectionItems, parmas);

        data_collectionItems = data1.dataList;
      }

      let data_resourceList: any[] = [];
      let auths: {
        itemId: string;
        isAuth: boolean;
      }[] = [];

      if (data_collectionItems.length > 0) {
        const { data: data_resourceList1 } = yield call(FServiceAPI.Resource.batchInfo, {
          resourceIds: data_collectionItems
            .map((d) => {
              return d.mountResourceInfo.resourceId;
            })
            .join(','),
        });
        data_resourceList = data_resourceList1;

        const list0: CollectionDetailPageModelState['collectionItems'] = data_collectionItems.map<
          CollectionDetailPageModelState['collectionItems'][number]
        >((i, j: number) => {
          const resource = data_resourceList.find((r) => {
            return r.resourceId === i.mountResourceInfo.resourceId;
          });
          // console.log(data_collectionItems[j], ' data.dataList[j] sdfsdfdsfsdf');
          return {
            itemID: i.itemId,
            itemTitle: i.itemTitle,
            itemSerial: i.sortId,
            itemNumber: i.number,
            resourceInfo: {
              id: resource.resourceId,
              cover: resource.coverImages.length > 0 ? resource.coverImages[0] : '',
              name: resource.resourceName,
              title: resource.resourceTitle,
              version: resource.latestVersion,
              policy: resource.policies
                .filter((l: any) => {
                  return l.status === 1;
                })
                .map((l: any) => l.policyName),
              type: resource.resourceType,
              status: resource.status,
              authProblem: false,
              updateDate: dayjs(resource.updateDate).format(FUtil.Predefined.momentDateFormat),
              username: resource.username,
              useAvatar: `${AVATAR_URL_PREFIX}${resource.userId}`,
              isChoice: resource.operationType === 5,
              intro: resource.intro,
              releaseDateTime: dayjs(resource.resourceVersions[0]?.createDate).format(
                'YYYY-MM-DD',
              ),
            },
          };
        });
        yield put<ChangeAction>({
          type: 'change',
          payload: {
            collectionItems: list0,
            collectionItems_totalCount: data.totalItem,
          },
        });

        const parmas1: Parameters<typeof FServiceAPI.Resource.getCollectionItemsAuth>[0] = {
          resourceId: collectionDetailPage.resource_ID,
          itemIds: data_collectionItems
            .map((d) => {
              return d.itemId;
            })
            .join(','),
        };
        const {
          ret,
          errCode,
          data: data1,
        }: {
          ret: number;
          errCode: number;
          data: {
            itemId: string;
            isAuth: boolean;
          }[];
        } = yield call(FServiceAPI.Resource.getCollectionItemsAuth, parmas1);

        if (ret !== 0 || errCode !== 0) {
          return;
        }

        auths = data1;
      }

      const list: CollectionDetailPageModelState['collectionItems'] = data_collectionItems.map<
        CollectionDetailPageModelState['collectionItems'][number]
      >((i, j: number) => {
        const resAuth = auths.find((dd) => {
          return dd.itemId === i.itemId;
        });

        const resource = data_resourceList.find((r) => {
          return r.resourceId === i.mountResourceInfo.resourceId;
        });
        // console.log(data_collectionItems[j], ' data.dataList[j] sdfsdfdsfsdf');
        return {
          itemID: i.itemId,
          itemTitle: i.itemTitle,
          itemSerial: i.sortId,
          itemNumber: i.number,
          resourceInfo: {
            id: resource.resourceId,
            cover: resource.coverImages.length > 0 ? resource.coverImages[0] : '',
            name: resource.resourceName,
            title: resource.resourceTitle,
            version: resource.latestVersion,
            policy: resource.policies
              .filter((l: any) => {
                return l.status === 1;
              })
              .map((l: any) => l.policyName),
            type: resource.resourceType,
            status: resource.status,
            authProblem: !!resAuth && !resAuth.isAuth,
            updateDate: dayjs(resource.updateDate).format(FUtil.Predefined.momentDateFormat),
            username: resource.username,
            useAvatar: `${AVATAR_URL_PREFIX}${resource.userId}`,
            isChoice: resource.operationType === 5,
            intro: resource.intro,
            releaseDateTime: dayjs(resource.resourceVersions[0]?.createDate).format(FUtil.Predefined.momentDateFormat),
          },
        };
      });
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          collectionItems: list,
          // collectionItems_totalCount: data.totalItem,
        },
      });
    },
  },
  reducers: {
    change(state, { payload }) {
      return {
        ...state,
        ...payload,
      };
    },
  },
  subscriptions: {
    setup({}) {},
  },
};

export default Model;
